import styled from "styled-components";
import { Country, ProductOrBrand } from "../model/alternativeTypes";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import deepCopy from "../util/deepCopy";
import { Label } from "../editor/EditorHelpers";
import CountryDropdown, { CountryType } from "../components/CountryDropdown";
import Checkbox from "../components/Checkbox";
import { PrimaryButton, SecondaryButton } from "../components/Button";
import { TextEditor } from "../components/TextEditor";


interface ProductEditorProps {
  isAvoid: boolean;
  flyerLocale: string;
  categoryName: string;
  product: ProductOrBrand;
  onSave: (save: ProductOrBrand) => void;
  onCancel: () => void;
}

const ProductEditor: React.FC<ProductEditorProps> = ({ categoryName, isAvoid, product, onSave, onCancel }) => {
  const [localProduct, setLocalProduct] = useState(deepCopy(product));
  const [error, setError] = useState("");
  const [triedSubmit, setTriedSubmit] = useState(false);
  const { t } = useTranslation();
  const isCustomProduct = product.categories.length === 0
  const isNew = !product.id

  const toggleIsLessEvilAlternative = () => {
    if (localProduct.isLessEvilAlternative) {
      setLocalProduct({
        ...localProduct,
        isLessEvilAlternative: false,
        ownerCountry: localProduct.ownerCountry === "US" ? "OT" : localProduct.ownerCountry
      })
    } else {
      setLocalProduct({
        ...localProduct,
        isLessEvilAlternative: true,
        ownerCountry: "US"
      })
    }
  }

  useEffect(() => {
    if (!localProduct.name) {
      setError(t('name-required.error'));
      return;
    }
    setError("");
  }, [localProduct]);

  const handleInvalidSubmit = (e) => {
    e.preventDefault();
    setTriedSubmit(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...localProduct,
      id: localProduct.id || localProduct.name,
      include: isNew ? true : localProduct.include
    });
  };

  return (
    <form onSubmit={error ? handleInvalidSubmit : handleSubmit}>
    <ProductEditorWrapper>
      {isCustomProduct ? (<>
        <TextEditor 
          requestFocus={isNew}
          label={t('product-or-brand-name.label') + " *"} 
          value={localProduct.name} 
          onChange={(newValue) => setLocalProduct({
            ...localProduct,
            name: newValue
          })}
        />
        <div>
          <Label>{t('country.label')}</Label>
          <CountryDropdown 
            countryTypes={isAvoid ? CountryType.RussiaFriendly : CountryType.EuropeAllyOrUs} 
            country={localProduct.ownerCountry} 
            onChange={(country) => setLocalProduct({
              ...localProduct,
              ownerCountry: country as Country
            })}
          />
        </div>
        {!isAvoid && <Checkbox 
          id={categoryName + ".less-evil." + localProduct.name}
          checked={localProduct.isLessEvilAlternative || false}
          onChange={() => toggleIsLessEvilAlternative()}
          label={t('less-evil-alternative.footnote')}
        />}
      </>) : (
        <div style={{ marginLeft: '2px' }}>{product.name}</div>
      )}
      <ButtonBar>
        <div>{triedSubmit && error}</div>
        <SecondaryButton 
          $small 
          type="button"
          style={{ marginLeft: 'auto' }}
          onClick={() => onCancel()}
        >
          {t('cancel.button')}
        </SecondaryButton>
        <PrimaryButton
          $small
          type="submit"
        >
          {t('save.button')}
          </PrimaryButton>
      </ButtonBar>
    </ProductEditorWrapper>
    </form>
  )
}

const ProductEditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
  padding: 10px;
  background-color: var(--side-bg-color);
  border-radius: 4px;
`;

const ButtonBar = styled.div`
  display: flex;
  gap: 10px;
`;


export default ProductEditor;